import { isOutsideExpectedRange } from "utils";

import { HARVEST_UNIT_VALUE } from "consts";

import { BulkUpdateCropYieldRequestBody, CropsWithMissingHistoricYieldDataByCropId } from "models";

import { needsHistoricYieldUpdate } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";

export type YieldCell = { cropId: string; year: number };

export type YieldCropMetadata = {
  imageUrl?: string;
  harvestTperHa?: PracticeManagement.ParameterBounds;
  isZeroYieldsAllowed: boolean;
};

export type YieldFormValues = Record<string, Record<string, number | null>>;

type YieldCellContext = {
  cropsWithMissingHistoricYieldDataByCropId: CropsWithMissingHistoricYieldDataByCropId;
  cropMetadata: Record<string, YieldCropMetadata>;
  values: YieldFormValues | undefined;
  initialValues: YieldFormValues;
  sanityNotes: Record<string, string>;
};

export type YieldCellIssues = {
  /** Outside the crop's expected range with no explanation — blocks saving entirely. */
  unexplainedOutOfRange: YieldCell[];
  /** No yield entered — blocks completing the task, but not saving progress for later. */
  missing: YieldCell[];
  /** Explicitly entered as 0, which needs confirming because it reduces the payout. */
  zero: YieldCell[];
};

export const sanityNoteKey = ({ cropId, year }: YieldCell) => `${cropId}|${year}`;

export const isYieldCellDisabled = (yearData: CropsWithMissingHistoricYieldDataByCropId[string]["years"][number]) =>
  !!yearData && yearData.isCompleted && !yearData.isPartOfSoilPlus3DataCollection;

/**
 * Derives the save-gate state straight from the entered values rather than from the form's validation state,
 * which only reflects cells rc-field-form has already validated and so lags behind what is on screen.
 */
export const collectYieldTableCellsStates = ({
  cropsWithMissingHistoricYieldDataByCropId,
  cropMetadata,
  values,
  initialValues,
  sanityNotes,
}: YieldCellContext): YieldCellIssues => {
  const issues: YieldCellIssues = { unexplainedOutOfRange: [], missing: [], zero: [] };

  Object.entries(cropsWithMissingHistoricYieldDataByCropId).forEach(([cropId, data]) => {
    Object.entries(data.years).forEach(([yearKey, yearData]) => {
      if (isYieldCellDisabled(yearData)) return;

      const year = Number(yearKey);
      const cell = { cropId, year };
      const value = values?.[cropId]?.[yearKey] ?? null;

      if (value === null || value === undefined) {
        issues.missing.push(cell);
        return;
      }

      if (isOutsideExpectedRange(value, cropMetadata[cropId]?.harvestTperHa) && !sanityNotes[sanityNoteKey(cell)]) {
        issues.unexplainedOutOfRange.push(cell);
      }

      // Only a newly entered zero needs confirming; one that was already recorded as zero has been confirmed before.
      if (value === 0 && cropMetadata[cropId]?.isZeroYieldsAllowed && initialValues[cropId]?.[yearKey] !== 0) {
        issues.zero.push(cell);
      }
    });
  });

  return issues;
};

/**
 * Builds the payload for the crop-years the farmer actually changed. A cleared cell is sent as an explicit null so
 * the deletion reaches the backend; a cell that was never filled in is left out entirely.
 */
export const buildBulkUpdateRequests = ({
  cropsWithMissingHistoricYieldDataByCropId,
  cropMetadata,
  values,
  initialValues,
  sanityNotes,
  initialSanityNotes,
}: YieldCellContext & { initialSanityNotes: Record<string, string> }): BulkUpdateCropYieldRequestBody[] => {
  const requests: BulkUpdateCropYieldRequestBody[] = [];

  Object.entries(values ?? {}).forEach(([cropId, yearValues]) => {
    const cropData = cropsWithMissingHistoricYieldDataByCropId[cropId];
    if (!cropData) return;

    Object.entries(yearValues ?? {}).forEach(([yearKey, enteredValue]) => {
      const year = Number(yearKey);
      const yearData = cropData.years[year];
      if (!yearData) return;

      const yieldPerHa = enteredValue ?? null;
      const initialValue = initialValues[cropId]?.[yearKey] ?? null;
      // Nothing to write for a cell that was empty before and still is — only for one the farmer just cleared.
      if (yieldPerHa === null && initialValue === null) return;

      const noteKey = sanityNoteKey({ cropId, year });
      const sanityNote = sanityNotes[noteKey];
      // An explanation only belongs to a value that is still outside the expected range, so a corrected or cleared
      // yield drops the stored note. Otherwise the key is left out entirely: the backend writes this field straight
      // through, so sending null for every untouched cell would wipe explanations saved elsewhere.
      const isStillOutsideExpectedRange = isOutsideExpectedRange(yieldPerHa, cropMetadata[cropId]?.harvestTperHa);
      let noteUpdate: Pick<BulkUpdateCropYieldRequestBody, "sanityNoteYield"> | undefined;
      if (isStillOutsideExpectedRange && sanityNote) {
        noteUpdate = { sanityNoteYield: sanityNote };
      } else if (!isStillOutsideExpectedRange && (sanityNote || initialSanityNotes[noteKey])) {
        noteUpdate = { sanityNoteYield: null };
      }

      yearData.entries.forEach((crop) => {
        if (!needsHistoricYieldUpdate(crop)) return;

        requests.push({
          farmFieldCropId: crop.id,
          yieldPerHa,
          // Historical data is all T/ha, only future data supports units
          yieldPerHaUnit: HARVEST_UNIT_VALUE.tonnesPerHa,
          ...noteUpdate,
        });
      });
    });
  });

  return requests;
};
